import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Load user on initial render if token exists
  useEffect(() => {
    const loadUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Set default auth header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        const res = await axios.get('/api/auth/me');
        
        setUser(res.data);
        setIsAuthenticated(true);
      } catch (err) {
        console.error('Error loading user:', err);
        localStorage.removeItem('token');
        setToken(null);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [token]);

  // Clear error
  const clearError = () => setError(null);

  // Register user
  const signup = async (name, email, password) => {
    clearError();
    
    try {
      const res = await axios.post('/api/auth/signup', { name, email, password });
      
      // Set token
      const newToken = res.data.token;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      // Set default auth header
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      // Set user and auth state
      setUser(res.data.user);
      setIsAuthenticated(true);
      
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Error during registration');
      return false;
    }
  };

  // Login user
  const login = async (email, password) => {
    clearError();
    
    try {
      const res = await axios.post('/api/auth/login', { email, password });
      
      // Set token
      const newToken = res.data.token;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      // Set default auth header
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      // Set user and auth state
      setUser(res.data.user);
      setIsAuthenticated(true);
      
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
      return false;
    }
  };

  // Logout user
  const logout = useCallback(() => {
    // Remove token
    localStorage.removeItem('token');
    setToken(null);
    
    // Remove auth header
    delete axios.defaults.headers.common['Authorization'];
    
    // Reset state
    setUser(null);
    setIsAuthenticated(false);
    clearError();
  }, []);

  // Get auth header for API requests
  const getAuthHeader = useCallback(() => {
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        loading,
        error,
        isAuthenticated,
        clearError,
        signup,
        login,
        logout,
        getAuthHeader,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};